import React, { Suspense, lazy, useEffect, useState } from 'react';
import { OPEN_COOKIE_SETTINGS_EVENT, loadConsent } from '../utils/consent';
import IdleMount from './IdleMount';

const CookieConsent = lazy(() => import('./CookieConsent'));

/**
 * The cookie banner's code loads only when it is needed: for a visitor who
 * has not chosen yet (or whose choice expired), or when "Cookie settings" is
 * opened from the footer or the privacy page. A stored choice needs no
 * banner: Consent Mode is updated in public/index.html and the trackers read
 * it through utils/consent (ConsentTrackers).
 * A first-time visitor's banner code waits for `load` + idle (it shows 1.2 s
 * after mounting anyway), so it never competes with the largest paint;
 * "Cookie settings" mounts it at once.
 */
const LazyCookieConsent = () => {
  const [state, setState] = useState(() => (loadConsent() ? null : { openSettings: false }));

  useEffect(() => {
    if (state) return undefined; // mounted: the banner listens itself
    const open = () => setState({ openSettings: true });
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, [state]);

  if (!state) return null;
  const banner = (
    <Suspense fallback={null}>
      <CookieConsent openSettings={state.openSettings} />
    </Suspense>
  );
  return state.openSettings ? banner : <IdleMount timeout={2000}>{banner}</IdleMount>;
};

export default LazyCookieConsent;
